// ============================================================
// YouTube URL 智慧解析
// 支援的網址形式：
//   https://youtu.be/VIDEO_ID?t=75
//   https://youtu.be/VIDEO_ID?list=...&t=75
//   https://www.youtube.com/watch?v=VIDEO_ID&t=1m15s
//   https://www.youtube.com/embed/VIDEO_ID?start=75
//   https://www.youtube.com/shorts/VIDEO_ID
// 支援的時間戳：75、75s、1m15s、1h2m3s
// ============================================================

export interface ParsedYouTubeLink {
  youtubeId: string
  startSeconds: number
}

export function parseYouTubeUrl(raw: string): ParsedYouTubeLink | null {
  try {
    const u = new URL(raw.trim())
    let id: string | null = null

    if (u.hostname.endsWith('youtu.be')) {
      id = u.pathname.slice(1).split(/[/?#]/)[0] || null
    } else if (u.pathname.startsWith('/watch')) {
      id = u.searchParams.get('v')
    } else {
      const m = u.pathname.match(/\/(embed|shorts|live)\/([\w-]{11})/)
      if (m) id = m[2]
    }

    if (!id || !/^[\w-]{11}$/.test(id)) return null

    const startSeconds = parseTimestamp(
      u.searchParams.get('t') ?? u.searchParams.get('start') ?? '0'
    )
    return { youtubeId: id, startSeconds }
  } catch {
    return null
  }
}

export function parseTimestamp(raw: string): number {
  if (!raw) return 0
  // 純數字：'75'
  if (/^\d+$/.test(raw)) return Number(raw)
  // YouTube 格式：'1h2m3s' / '1m15s' / '75s'
  let total = 0
  const h = raw.match(/(\d+)h/)
  const m = raw.match(/(\d+)m/)
  const s = raw.match(/(\d+)s/)
  if (h) total += Number(h[1]) * 3600
  if (m) total += Number(m[1]) * 60
  if (s) total += Number(s[1])
  return total
}

export async function fetchYouTubeMeta(
  id: string
): Promise<{ title: string; thumbnail: string | null }> {
  try {
    const res = await fetch(
      `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(
        `https://youtu.be/${id}`
      )}`
    )
    if (!res.ok) return { title: '', thumbnail: null }
    const json = (await res.json()) as { title: string; thumbnail_url?: string }
    return { title: json.title ?? '', thumbnail: json.thumbnail_url ?? null }
  } catch {
    return { title: '', thumbnail: null }
  }
}
